// ============================================================
// RangeRail - the range-band instrument (v8.233.0).
//
// Five detents (PB / S / N / L / E) with their modifiers, replacing
// the Range dropdown. AUTO is the resting state: the band the map
// resolved (or Normal without a map line) glows softly; clicking a
// detent LOCKS it bright, the AUTO cap hands control back. A live
// measurement prints its metres; a measured shot past three times
// the weapon's range stencils BEYOND RANGE (advisory - the roll
// still resolves at Normal, exactly as before). Melee weapons have
// no bands: the rail yields to a MELEE stencil. While positions do not
// count (the GM's switch) there is no map to follow: AUTO gives way to
// BY HAND, and an unpicked rail rests on Normal as it always has.
// ============================================================
import { RANGE_MODS, RANGE_LABELS } from '../../engine/attackCalc';
import clsx from '../../utils/clsx';
import styles from '../../styles/TargetingConsole.module.css';

const BANDS: { key: string; short: string }[] = [
  { key: 'pointBlank', short: 'PB' },
  { key: 'short', short: 'S' },
  { key: 'normal', short: 'N' },
  { key: 'long', short: 'L' },
  { key: 'extreme', short: 'E' },
];

const signed = (v: number) => (v > 0 ? `+${v}` : String(v));

interface Props {
  /** The user's choice - null/'auto' means "whatever the map says". */
  rangeBandChoice: string | null;
  /** What the map resolved, when a live line exists. */
  rangeBandFromMap: string | null;
  /** Live measured distance in metres, when the map has a line. */
  mapMetres: number | null;
  setRangeBand: (band: string) => void;
  isMelee: boolean;
  /** Positions do not count at this table: there is no map to follow, the band is picked by hand. */
  byHand?: boolean;
  disabled?: boolean;
}

export default function RangeRail({
  rangeBandChoice,
  rangeBandFromMap,
  mapMetres,
  setRangeBand,
  isMelee,
  byHand = false,
  disabled = false,
}: Props) {
  if (isMelee) {
    return (
      <div className={styles.rail} data-testid="range-rail">
        <span className={styles.railTitle}>RANGE</span>
        <span className={styles.stencilDim}>MELEE</span>
      </div>
    );
  }
  const auto = !rangeBandChoice || rangeBandChoice === 'auto';
  // Measured but beyond every band: no band resolves at all.
  const beyondRange = mapMetres != null && rangeBandFromMap === null;
  const glowBand = auto ? (rangeBandFromMap ?? 'normal') : null;
  return (
    <div className={styles.rail} data-testid="range-rail">
      <span className={styles.railTitle}>RANGE</span>
      {BANDS.map((b) => (
        <RangeNotch
          key={b.key}
          band={b}
          locked={rangeBandChoice === b.key}
          glowing={glowBand === b.key}
          onPick={() => setRangeBand(b.key)}
          disabled={disabled}
        />
      ))}
      {byHand ? (
        <span className={styles.stencilDim} data-testid="range-by-hand" title="Positions do not count: pick the band">
          BY HAND
        </span>
      ) : (
        <button
          type="button"
          data-testid="range-auto"
          className={clsx(styles.autoCap, auto && styles.autoCapEngaged)}
          aria-pressed={auto}
          title={auto ? autoTitle(glowBand, rangeBandFromMap) : 'Return to the map-resolved band'}
          onClick={() => setRangeBand('auto')}
          disabled={disabled}
        >
          AUTO
        </button>
      )}
      {mapMetres != null && <span className={styles.metres}>{Math.round(mapMetres)}m</span>}
      {beyondRange && <span className={styles.beyond}>BEYOND RANGE</span>}
    </div>
  );
}

/** "Auto - Long (map)", or "(no map line)" when the band is the Normal default. */
function autoTitle(glowBand: string | null, fromMap: string | null): string {
  return `Auto - ${RANGE_LABELS[glowBand ?? 'normal']}${fromMap ? ' (map)' : ' (no map line)'}`;
}

interface NotchProps {
  band: { key: string; short: string };
  /** Chosen by hand - lit bright. */
  locked: boolean;
  /** The band AUTO resolves to - glowing softly. */
  glowing: boolean;
  onPick: () => void;
  disabled: boolean;
}

function RangeNotch({ band, locked, glowing, onPick, disabled }: NotchProps) {
  return (
    <button
      type="button"
      data-testid={`range-notch-${band.key}`}
      className={clsx(styles.notch, locked && styles.notchLocked, glowing && styles.notchGlow)}
      aria-pressed={locked}
      title={`${RANGE_LABELS[band.key]}${glowing ? ' - map-resolved' : ''}`}
      onClick={onPick}
      disabled={disabled}
    >
      {band.short} {signed(RANGE_MODS[band.key] ?? 0)}
    </button>
  );
}
